import { useState, type FC, useEffect, useMemo } from 'react';
import Icon from '2-core-elements/Icon/Icon';
import useSetCardStatusMutation from './Utils/useSetCardStatusMutation';
import type { CardIDDataType } from './Utils/useCardIDsQuery';
import { CARD_STATUS_ACTIVE, CARD_STATUS_INACTIVE } from './constants';

interface AccountOption {
    title: string;
    icon?: string;
    link?: string;
    isActive?: boolean;
}

type InlineMsg = { status: 'active' | 'inactive' } | null;

const CardSettingsOptions: FC<{
    card: CardIDDataType;
    setInlineMessage: (msg: InlineMsg) => void;
    onStatusChange?: () => void;
    changePinUrl: string;
    reportCardUrl: string;
    anyCardStatusZero?: boolean;
}> = ({ card, setInlineMessage, onStatusChange, changePinUrl, reportCardUrl, anyCardStatusZero }) => {
    const [isSwitchLoading, setIsSwitchLoading] = useState(false);
    const setCardStatusMutation = useSetCardStatusMutation();

    // Inject mock options for demo
    const options = useMemo<AccountOption[]>(() => {
        return [
            {
                title: 'MCC Plus Card ',
                icon: 'settings',
                isActive: card.status === CARD_STATUS_ACTIVE,
            },
            {
                title: 'Change Pin',
                icon: 'lock',
                link: changePinUrl,
            },
            {
                title: 'Report Card as Lost, Stolen or Damaged.',
                icon: 'error-circle',
                link: reportCardUrl,
            }
        ];
    }, [card.status, changePinUrl, reportCardUrl]);

    // Manage local status state, initialized from card.status
    const [localStatus, setLocalStatus] = useState(card.status);
    // Update localStatus if card.status changes from parent (e.g. after refetch)
    useEffect(() => {
        setLocalStatus(card.status);
    }, [card.status]);

    // Hide inlineMessage when card changes (e.g. dropdown swap)
    useEffect(() => {
        setInlineMessage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card.id]);

    const isActive = localStatus === CARD_STATUS_ACTIVE && !anyCardStatusZero;

    const handleSwitchClick = async(): Promise<void> => {
        if (localStatus !== CARD_STATUS_ACTIVE && localStatus !== CARD_STATUS_INACTIVE) {
            return;
        }
        setIsSwitchLoading(true);

        const newStatus = localStatus === CARD_STATUS_ACTIVE ? CARD_STATUS_INACTIVE : CARD_STATUS_ACTIVE;
        await setCardStatusMutation.mutateAsync({
            cardId: card.id,
            newStatus,
        });
        setLocalStatus(newStatus); // update local state immediately for snappy UI
        setInlineMessage({ status: newStatus === CARD_STATUS_ACTIVE ? 'active' : 'inactive' });
        if (onStatusChange) {
            onStatusChange();
        }

        setIsSwitchLoading(false);
    };

    return (
        <section className="account-options" aria-label="Account Options">
            {options.map((option, optionIndex) => {
                const isDisabled = (card.status !== CARD_STATUS_ACTIVE && card.status !== CARD_STATUS_INACTIVE) || !!anyCardStatusZero;
                if (optionIndex === 0) {
                    return (
                        <div className={`account-option${isDisabled ? ' disabled' : ''}`} key={option.title}>
                            <div className="account-option-content">
                                <Icon icon={option.icon ?? ''} className="account-option-icon" />
                                <div className="account-option-title">
                                    {option.title}{' '}{optionIndex === 0 ? (isActive ? 'Active' : 'Inactive') : ''}
                                </div>
                            </div>
                            <div className="account-option-extra-content">
                                <span>{isActive ? 'Active' : 'Inactive'}</span>
                                <button
                                    type="button"
                                    className={`active-switch${isActive ? ' active' : ''}`}
                                    aria-pressed={isActive}
                                    aria-label="Toggle MCC Plus Card Status"
                                    disabled={isSwitchLoading || (localStatus !== CARD_STATUS_ACTIVE && localStatus !== CARD_STATUS_INACTIVE) || anyCardStatusZero}
                                    onClick={handleSwitchClick}
                                >
                                    <div className="active-switch-inner" />
                                </button>
                            </div>
                        </div>
                    );
                }
                return (
                    <a
                        href={option.link}
                        className={`account-option${isDisabled ? ' disabled' : ''}`}
                        key={option.title}
                        aria-disabled={isDisabled}
                        tabIndex={isDisabled ? -1 : 0}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                            if (isDisabled) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <div className="account-option-content">
                            <Icon icon={option.icon ?? ''} className="account-option-icon" />
                            <div className="account-option-title">{option.title}</div>
                        </div>
                        <div className="account-option-extra-content">
                            <Icon icon="chevron-right" className="option-suffix-icon" />
                        </div>
                    </a>
                );
            })}
        </section>
    );
};

export default CardSettingsOptions;
